import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { mgcIconCheckRegular } from '@sb/mgc-icons/mgcIcon-check_regular.icon';
import { mgcIconForbidCircleRegular } from '@sb/mgc-icons/mgcIcon-forbid_circle_regular.icon';
import { mgcIconLockRegular } from '@sb/mgc-icons/mgcIcon-lock_regular.icon';
import { mgcIconSkipForwardRegular } from '@sb/mgc-icons/mgcIcon-skip_forward_regular.icon';
import { mgcIconWarningRegular } from '@sb/mgc-icons/mgcIcon-warning_regular.icon';

import { IconRegistry } from '../icon/icon-registry.service';
import { IconComponent } from '../icon/icon.component';

/** How far a step has got. Says nothing about whether the user is standing on it. */
export type StepStatus = 'completed' | 'warning' | 'skipped' | 'pending' | 'disabled' | 'locked';
export type StepperOrientation = 'horizontal' | 'vertical';
export type StepperVariant = 'default' | 'pill';

/**
 * Which steps are reachable by click.
 *
 * - `'none'` — none of them; the stepper is a progress display.
 * - `'visited'` — anywhere the user has already been, plus the one step in front of them.
 * - `'all'` — every step but the one they are on, for a flow whose order is a suggestion rather
 *   than a prerequisite chain.
 */
export type StepperClickMode = 'none' | 'visited' | 'all';

/** Which marker a step draws, derived from its status and whether it is the current one. */
type StepMarker = 'check' | 'warning' | 'skip' | 'current' | 'number' | 'disabled' | 'locked';

export interface Step {
  label: string;
  /**
   * How far this step has got. When every step omits this field, the stepper falls
   * back to linear progress (any index below `currentStep` is treated as completed).
   * Set it on at least one step to switch the stepper into explicit mode, where an
   * omitted status means `pending`.
   */
  status?: StepStatus;
  /** Second line under the label. Already translated by the consumer. */
  subText?: string;
}

interface RenderedStep {
  label: string;
  subText?: string;
  status: StepStatus;
  marker: StepMarker;
  clickable: boolean;
  isCurrent: boolean;
  pillClasses: string;
}

// A skipped step counts as "already been past" for `lastCompleted` — otherwise it would strand the
// next pending step, unreachable via the `lastCompleted + 1` rule. This only feeds `lastCompleted`;
// the step's own status is whatever the consumer set, so it never renders as completed.
function isStepCompleted(step: Step, index: number, explicitMode: boolean, current: number): boolean {
  return explicitMode
    ? step.status === 'completed' || step.status === 'warning' || step.status === 'skipped'
    : index < current;
}

function getStepStatus(step: Step, index: number, current: number, explicitMode: boolean): StepStatus {
  return step.status ?? (isStepCompleted(step, index, explicitMode, current) ? 'completed' : 'pending');
}

// Where the user is standing outranks how far the step got, so the marker says "you are here"
// rather than showing a tick or a skip glyph for the step they are on. The status marker comes
// back once they move away.
//
// 'locked' is the exception, and outranks even that: it is not a degree of progress the current
// marker can stand in for but a statement that the step is unavailable, and it is the only status
// whose glyph is also the affordance for its click. Drawing "you are here" over it would leave a
// consumer that parks `currentStep` on a locked step with neither the lock nor a way to reach the
// upgrade prompt.
function getStepMarker(status: StepStatus, isCurrent: boolean): StepMarker {
  if (status === 'locked') return 'locked';
  if (isCurrent) return 'current';
  if (status === 'skipped') return 'skip';
  if (status === 'disabled') return 'disabled';
  if (status === 'warning') return 'warning';
  return status === 'completed' ? 'check' : 'number';
}

function getPillClasses(marker: StepMarker, status: StepStatus, isCurrent: boolean): string {
  if (isCurrent) return 'px-md bg-brand text-brand-inverted';
  if (marker === 'check') return 'w-9 bg-brand-subtle text-brand-strong';
  if (marker === 'warning') return 'w-9 bg-warning-subtle text-warning-strong';

  return status === 'disabled' ? 'w-9 bg-surface text-disabled' : 'w-9 bg-surface text-default';
}

/** The boolean form predates the named modes and still spells the two states it always meant. */
function toClickMode(clickable: boolean | StepperClickMode): StepperClickMode {
  if (clickable === true) return 'visited';
  if (clickable === false) return 'none';

  return clickable;
}

function isStepClickable(
  status: StepStatus,
  index: number,
  current: number,
  lastCompleted: number,
  mode: StepperClickMode,
): boolean {
  // 'locked' is deliberately not short-circuited alongside 'disabled': a locked step's click is
  // the whole point of the state — it is what the consumer turns into an upgrade prompt — so it
  // follows the same reachability rules as any other step and stays silent only under 'none'.
  if (status === 'disabled') return false;
  if (mode === 'none') return false;
  // Where the user already is is never a button — except a locked step, whose click asks for the
  // feature rather than moving anywhere, and so is worth offering wherever the step is drawn.
  if (index === current && status !== 'locked') return false;
  // A flow whose steps carry no prerequisite — each one is somewhere to go and look, not a form
  // that has to be filled before the next opens — opens every step at once instead.
  if (mode === 'all') return true;
  // Completed and skipped are both "already been past", and either is fair to jump back to. A
  // skipped step needs saying explicitly: it sits well behind `lastCompleted + 1`, the slot the
  // rule below reserves for the next pending step, so it would never qualify on its own.
  if (status === 'completed' || status === 'warning' || status === 'skipped') return true;
  return lastCompleted >= 0 && index === lastCompleted + 1;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'sb-stepper',
  imports: [IconComponent, TranslatePipe, NgTemplateOutlet],
  templateUrl: './stepper.component.html',
  host: {
    class: 'block w-full',
  },
})
export class StepperComponent {
  steps = input.required<Step[]>();
  currentStep = input<number>(0);
  ariaLabel = input<string>();
  orientation = input<StepperOrientation>('horizontal');
  variant = input<StepperVariant>('default');
  // `false`/`true` are the original two-state form and keep their meaning ('none'/'visited'); the
  // named modes exist so the widest one can be asked for without a second, only-meaningful-in-
  // combination input.
  clickable = input<boolean | StepperClickMode>(false);
  // A consumer rendering its own progress bar suppresses this one, so a screen reader hears one
  // progress value rather than two that measure different things. The label then moves onto the
  // step list, the only thing left to carry it.
  hideProgressBar = input(false);

  stepClick = output<number>();

  constructor() {
    inject(IconRegistry).registerIcons([
      mgcIconCheckRegular,
      mgcIconSkipForwardRegular,
      mgcIconForbidCircleRegular,
      mgcIconLockRegular,
      mgcIconWarningRegular,
    ]);
  }

  renderedSteps = computed<RenderedStep[]>(() => {
    const current = this.currentStep();
    const clickMode = toClickMode(this.clickable());
    const steps = this.steps();
    // A step claiming `completed`, `pending` or `disabled` means the consumer is tracking
    // completion itself, so an omitted status elsewhere means "pending". `skipped` deliberately
    // does not count: passing one step over says nothing about how the rest are tracked, and
    // flipping the mode on it would strand every other step at pending. `disabled` and `locked`
    // both count — only a consumer tracking statuses itself can produce either, and a flow whose
    // one actionable step happens to be skipped would otherwise fall back to index-based progress
    // and draw every unavailable step as completed.
    const explicitMode = steps.some(
      (step) =>
        step.status === 'completed' ||
        step.status === 'warning' ||
        step.status === 'pending' ||
        step.status === 'disabled' ||
        step.status === 'locked',
    );
    // Highest index whose step is completed — drives "lastCompleted + 1 is also clickable".
    const lastCompleted = steps.reduce<number>(
      (max, step, idx) => (isStepCompleted(step, idx, explicitMode, current) ? idx : max),
      -1,
    );

    return steps.map((step, index) => {
      const status = getStepStatus(step, index, current, explicitMode);
      const isCurrent = index === current;
      const marker = getStepMarker(status, isCurrent);

      return {
        label: step.label,
        subText: step.subText,
        status,
        marker,
        clickable: isStepClickable(status, index, current, lastCompleted, clickMode),
        isCurrent,
        pillClasses: getPillClasses(marker, status, isCurrent),
      };
    });
  });

  progressPercentage = computed(() => {
    const current = this.currentStep();
    const totalSteps = this.steps().length;

    if (totalSteps <= 1) {
      return 0;
    }
    const raw = (current / (totalSteps - 1)) * 100;
    return Math.max(0, Math.min(100, raw));
  });

  edgeInset = computed(() => {
    const totalSteps = this.steps().length;
    return totalSteps > 0 ? `${50 / totalSteps}%` : '0%';
  });
}
